<template>
    <list-container fixed>
        <template #header>
            <list-cell-header :width="8">
                <template #title>
                    Kanaal
                </template>
            </list-cell-header>

            <list-cell-header :width="11">
                <template #title>
                    Pijplijn
                </template>
            </list-cell-header>

            <list-cell-header :width="12">
                <template #title>
                    Binnenkomst
                </template>

                <template #subtitle>
                    Laatste update
                </template>
            </list-cell-header>

            <list-cell-header :width="15">
                <template #title>
                    Fase
                </template>

                <template #subtitle>
                    Reden
                </template>
            </list-cell-header>

            <list-cell-header :width="20">
                <template #title>
                    Aangemaakt
                </template>

                <template #subtitle>
                    Team
                </template>
            </list-cell-header>

            <list-cell-header :width="20">
                <template #title>
                    Verantwoordelijk
                </template>

                <template #subtitle>
                    Team
                </template>
            </list-cell-header>

            <list-cell-header>
                <template #title>
                    <label>
                        <select v-model="selectedView">
                            <option value="score">Kans</option>
                            <option value="appointment">Afspraak</option>
                            <option value="task">Taak</option>
                            <option value="valuation">Woningwaarde</option>
                            <!--
                            <option value="last-viewed">Laatst bekeken</option>
                            -->
                        </select>
                    </label>
                </template>
            </list-cell-header>

            <list-cell-header />
        </template>

        <template #content>
            <list-row
                v-for="lead in filteredLeads"
                :key="lead.id.toString()"
            >
                <list-cell-channel
                    :channel="lead.origin.channel"
                />

                <list-cell-pipeline-and-status
                    :pipeline="lead.pipeline"
                    :status="lead.status"
                />

                <list-cell-created-updated
                    :created-at="lead.createdAt"
                    :updated-at="lead.updatedAt"
                />

                <list-cell-bar :color="phaseColorForLead(lead)">
                    <template #title>
                        {{ lead.phase?.name ?? '-' }}
                    </template>

                    <template #subtitle>
                        {{ lead.reason?.name ?? '-' }}
                    </template>
                </list-cell-bar>

                <list-cell-text>
                    <template #content>
                        <user-team-block
                            :user="lead.referrer.user"
                            :team="lead.referrer.team"
                        />
                    </template>
                </list-cell-text>

                <list-cell-text>
                    <template #content>
                        <user-team-block
                            :user="lead.assignee.user"
                            :team="lead.assignee.team"
                        />
                    </template>
                </list-cell-text>

                <list-cell-score
                    v-if="selectedView === 'score'"
                    :score="lead.score"
                />

                <list-cell-next-or-previous-appointment
                    v-else-if="selectedView === 'appointment'"
                    :appointment="lead.nextOrPreviousAppointment"
                />

                <list-cell-next-or-previous-task
                    v-else-if="selectedView === 'task'"
                    :task="lead.nextOrPreviousTask"
                />

                <template v-else-if="selectedView === 'valuation'">
                    <list-cell-text>
                        <template #content>
                            <template v-if="lead.manualValuation">
                                {{ formatMoney(lead.manualValuation) }}
                            </template>
                            <template v-else-if="lead.medianValuation">
                                {{ formatMoney(lead.medianValuation) }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                    </list-cell-text>
                </template>

                <template v-else-if="selectedView === 'last-viewed'">
                    <list-cell-text>
                        <template #content>
                            Laatst bekeken
                        </template>
                    </list-cell-text>
                </template>

                <list-cell-action-open @click="openLead(lead)" />
            </list-row>
        </template>
    </list-container>

    <list-footer>
        <template #default>
            <collapse-box-decorator trigger="click">
                <template #default="{ bindings }">
                    <button-text v-bind="bindings">
                        <svg-icon :icon="IconAdd" />

                        <span>Toevoegen aan andere pijplijn</span>
                    </button-text>
                </template>

                <template #title>
                    Kies de pijplijn
                </template>

                <template #content>
                    <collapse-option-container>
                        <collapse-option
                            v-for="pipeline in pipelines.data"
                            :key="pipeline.id.toString()"
                            @click="addToPipeline(pipeline)"
                        >
                            <pipeline-company-block :pipeline="pipeline" />
                        </collapse-option>
                    </collapse-option-container>
                </template>
            </collapse-box-decorator>
        </template>
        <template #right>
            <list-footer-action-show-more
                v-if="availableLeads.length > 3"
                @click="viewAll = !viewAll"
            >
                <span v-if="viewAll">Bekijk minder</span>
                <span v-else>Bekijk alle {{ leadCollection?.count.value }}</span>
            </list-footer-action-show-more>
        </template>
    </list-footer>
</template>

<script setup lang="ts">
    import { computed, inject, ref } from 'vue';
    import { Pipeline, type Lead, Contact, type CompanySubResource } from '@keyvue/one-api-client';

    import {
        Color,
        SvgIcon,
        ButtonText,
        ListContainer,
        ListCellHeader,
        ListRow,
        ListCellText,
        ListCellBar,
        ListCellActionOpen,
        CollapseBoxDecorator,
        ListFooter,
        CollapseOption,
        CollapseOptionContainer,
        ListFooterActionShowMore,
        NotificationBlock,
    } from '@keyvue/one-ui';

    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import ListCellScore from '@components/List/Cells/ListCellScore.vue';
    import ListCellPipelineAndStatus from '@components/List/Cells/ListCellPipelineAndStatus.vue';
    import ListCellChannel from '@components/List/Cells/ListCellChannel.vue';
    import ListCellCreatedUpdated from '@components/List/Cells/ListCellCreatedUpdated.vue';
    import ListCellNextOrPreviousAppointment from '@components/List/Cells/ListCellNextOrPreviousAppointment.vue';
    import ListCellNextOrPreviousTask from '@components/List/Cells/ListCellNextOrPreviousTask.vue';
    import PipelineCompanyBlock from '@components/Blocks/PipelineCompanyBlock.vue';
    import UserTeamBlock from '@components/Blocks/UserTeamBlock.vue';
    import { useRouter } from 'vue-router';
    import useClient, { type ReactiveCollection } from '@/composables/useClient';
    import useLeadSecurity from '@composables/security/useLeadSecurity';
    import useNotify from '@composables/useNotify';
    import { formatMoney } from '@/utils/money';

    type PipelineListViewType =
        | 'score'
        | 'appointment'
        | 'task'
        | 'valuation'
    // | 'last-viewed'
    ;

    const contact = inject<Contact>('contact');
    if (!contact) throw new Error('contact is not provided');

    const leadCollection = inject<ReactiveCollection<Lead>>('leadCollection');
    if (!leadCollection) throw new Error('leadCollection is not provided');
    const availableLeads = computed(() => leadCollection.data.value);
    const filteredLeads = computed(() => (viewAll.value ? availableLeads.value : availableLeads.value.slice(0, 3)));

    const router = useRouter();
    const { client, currentCompany } = useClient();
    const { forLead: leadSecurityForLead } = useLeadSecurity();
    const { notify } = useNotify();

    if (!currentCompany.value) {
        throw new Error('Not authenticated');
    }

    const viewAll = ref(false);

    const pipelines = await client.getSubResources<CompanySubResource, Pipeline>(
        currentCompany.value,
        Pipeline,
        'available-pipelines',
    );

    const phaseColorForLead = (lead: Lead) => lead.phase?.color as Color || Color.gray400;
    const selectedView = ref<PipelineListViewType>('score');

    const canOpenLead = (lead: Lead) => {
        return leadSecurityForLead(lead).canViewDetail.value;
    };

    const openLead = async (lead: Lead) => {
        if (!canOpenLead(lead)) {
            notify({
                component: NotificationBlock,
                props: {
                    color: Color.orange500,
                    title: 'Geen toegang tot deze lead',
                    text: 'Vraag een teamlid van het andere team om toegang te verlenen',
                },
                duration: 3000,
            });

            return;
        }

        router.push({
            name: 'contactLeadDetail',
            params: {
                contactId: contact.id.toString(),
                leadId: lead.id.toString(),
            },
        });
    };

    const addToPipeline = (pipeline: Pipeline) => {
        router.push({
            name: 'duplicateLead',
            params: {
                contactId: contact.id.toString(),
                pipelineId: pipeline.id.toString(),
            },
        });
    };
</script>

<style scoped lang="scss">
.footer-actions {
    padding: size(md);
}
</style>
